import React, { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { ProductCard } from "@/features/product/ProductCard";
import { ClickableCard } from "../../ClickableCard";
import { useSelectedItemContext } from "../../SelectedItemContext";
import { Page, PortalDrawer, EditUpcomingOrderDrawer } from "..";
import { DrawerPageProps } from "../types";
import { Card } from "@/components/Card";
import { productSelect } from "@/store";
import {
  getDisplayDate,
  droppsProductToProductCardProduct,
} from "../../helpers";

type EditSubscriptionPageProps = { initialPage?: Page } & DrawerPageProps;

const EditSubscriptionPage = ({
  initialPage,
  ...props
}: EditSubscriptionPageProps) => {
  const order = useSelectedItemContext().boxItem;
  const { rechargeItem, address } = order;
  const cadence = rechargeItem.cadence;

  const [openedDrawer, setOpenedDrawer] = useState<Page | undefined>();
  const [localInit] = useState(initialPage);
  const droppsProductMap = droppsProductToProductCardProduct(order);

  // If given initial page, render it closed at first so that we can open it
  // with a smooth animation
  useEffect(() => {
    setOpenedDrawer(localInit);
  }, [localInit]);

  return (
    <>
      <ClickableCard
        title="Product Details"
        onClick={() => setOpenedDrawer({ kind: "edit-product" })}
      >
        <ProductCard
          className="-m-4"
          productCardValue={{
            ...droppsProductMap,
            size: `${order.rechargeItem.quantity} x ${droppsProductMap.size}`,
            price: order.rechargeItem.price.mul(order.rechargeItem.quantity),
          }}
          compact={true}
          hoverable={false}
          productTitle={
            <h3 data-id="product-card-title" className="title text-sm">
              {productSelect.name(order.product)}
            </h3>
          }
        />
      </ClickableCard>
      <ClickableCard
        title="Shipping Schedule"
        onClick={() => setOpenedDrawer({ kind: "update-interval" })}
        footer={
          cadence && (
            <Card.Footer>
              <p className="body text-sm">
                Delivery frequency every{" "}
                <span className="font-semibold underline">
                  {cadence.frequency}{" "}
                  {cadence.unit.getUnitForValue(cadence.frequency)}
                </span>
              </p>
            </Card.Footer>
          )
        }
      >
        <>
          <h4 className="body text-sm">Ship Date</h4>
          <p className="body text-3xl font-semibold text-primary xl:text-2xl">
            {getDisplayDate(rechargeItem.nextChargeDate)}
          </p>
        </>
      </ClickableCard>
      <ClickableCard
        title="Shipping Address"
        onClick={() => {
          setOpenedDrawer({ kind: "update-address" });
        }}
      >
        <>
          <p className="title text-sm">
            {address.firstName}
            {address.lastName ? ` ${address.lastName}` : ""}
            {address.company ? `, ${address.company}` : ""}
          </p>
          <p className="body text-sm">{address.address1}</p>
          <p className="body text-sm">{address.address2}</p>
          <p className="body text-sm">
            {address.city}, {address.province} {address.zip}
          </p>
        </>
      </ClickableCard>
      <div className="w-full">
        <Button
          className="w-full"
          variant="outline"
          onClick={() =>
            setOpenedDrawer({
              kind: "delay-shipment",
            })
          }
        >
          {"Delay Ship Date"}
        </Button>
        {rechargeItem.status !== "ONETIME" ? (
          <Button
            className="mt-4 w-full"
            variant="ghost"
            component="a"
            href={`/tools/recurring/pages/${window.RechargeCustomerInfo?.hash}/subscriptions/${rechargeItem.id}/cancel?token=${window.RechargeCustomerInfo?.token}`}
          >
            Cancel Subscription
          </Button>
        ) : (
          <Button
            className="mt-4 w-full"
            variant="ghost"
            onClick={() => setOpenedDrawer({ kind: "cancel-subscription" })}
          >
            Remove Item
          </Button>
        )}
      </div>
      <PortalDrawer
        open={openedDrawer !== undefined}
        title={
          openedDrawer?.kind === "swap-item" ? "Swap Product" : "Edit Item"
        }
        contents={(nav) =>
          openedDrawer !== undefined ? (
            <EditUpcomingOrderDrawer
              page={openedDrawer}
              {...nav}
              nextChargeDate={props.nextChargeDate}
            />
          ) : null
        }
        {...props}
        onBack={() => setOpenedDrawer(undefined)}
        transparentBackdrop={true}
      />
    </>
  );
};

export { EditSubscriptionPage };
